import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '@/hooks/useAuth';

/**
 * Wraps protected routes. Three behaviors based on AuthContext state:
 *   - `loading`: show a brief loading state (avoids a flash of /login while
 *     /api/auth/me is still in flight on first render)
 *   - `unauthenticated`: redirect to /login, preserving the attempted URL so
 *     we can land them there after sign-in
 *   - `authenticated`: render children
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { state } = useAuth();
  const location = useLocation();

  if (state.status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-kw-bg">
        <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
      </div>
    );
  }

  if (state.status === 'unauthenticated') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
